/**
 * Below are the colors that are used in the app. The colors are defined in the light and dark mode.
 * There are many other ways to style your app. For example, [Nativewind](https://www.nativewind.dev/), [Tamagui](https://tamagui.dev/), [unistyles](https://reactnativeunistyles.vercel.app), etc.
 */

const tintColorLight = '#0a7ea4';
const tintColorDark = '#fff';

// color_highlight: "#ccf3df",
// color_white: "#fff",
// color_black: "#2d3436",
// color_black_opaque: "rgba(0, 0, 0, 0.8)",
// color_black_opaquest: "rgba(0, 0, 0, 0.3)",

// if switching to a light theme, screw the default light grey accent color, go with blue

export const ThemeNames = [
  "white",
  "ocean",
  "pastel",
  "mint",
  "paper",
  "sepia",
  "muted",
  "night",
  "smoke",
  "black",
]

export const isDarkTheme = (theme) => {
  const darkThemes = ["sepia", "muted", "night", "smoke", "black"];
  return darkThemes.includes(theme);
}

export const ACCENT_COLORS = [
  { id: 'multicolor', color: 'linear-gradient(45deg, #FF0000, #00FF00, #0000FF)' },
  { id: 'blue', color: '#1a87ff' },
  { id: 'purple', color: '#AF52DE' },
  { id: 'pink', color: '#FF69B4' },
  { id: 'red', color: '#FF3B30' },
  { id: 'orange', color: '#FF9500' },
  { id: 'yellow', color: '#FFCC00' },
  { id: 'green', color: '#34C759' },
  { id: 'gray', color: '#8E8E93' },
  { id: 'black', color: '#000000' },
  { id: 'white', color: '#D4D4D4' },
];

export const NEUTRAL_ACCENT_IDS = new Set(['multicolor', 'gray', 'black', 'white']);

export const ACCENT_TEXT_COLORS = [
  { id: 'white', color: '#FFFFFF' },
  { id: 'black', color: '#000000' },
  { id: 'gray', color: '#64748B' },
  { id: 'beige', color: '#F5F5DC' },
  { id: 'cream', color: '#FFFDD0' },
];

const NEUTRAL_BANNER = {
  accent: { light: '#040404', dark: '#FFFFFF' },
  gradient: { light: ['#040404', '#040404'], dark: ['#FFFFFF', '#FFFFFF'] },
};
export const BANNER_CONTENT_COLORS = {
  'promo-code': NEUTRAL_BANNER,
  feature: NEUTRAL_BANNER,
  announcement: NEUTRAL_BANNER,
};
export const getBannerAccent = (contentType, isDark) => isDark ? '#FFFFFF' : '#040404';
export const getBannerGradient = (contentType, isDark) => NEUTRAL_BANNER.gradient[isDark ? 'dark' : 'light'];

const hexToRgb = (hex) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return { r, g, b };
};

export const getBannerBg = (contentType, isDark, colors) => {
  const accent = getBannerAccent(contentType, isDark, colors);
  const { r, g, b } = hexToRgb(accent);
  return `rgba(${r}, ${g}, ${b}, ${isDark ? 0.12 : 0.08})`;
};

export const getBannerBorder = (contentType, isDark, colors) => {
  const accent = getBannerAccent(contentType, isDark, colors);
  const { r, g, b } = hexToRgb(accent);
  return `rgba(${r}, ${g}, ${b}, ${isDark ? 0.25 : 0.2})`;
};

// Compatibility tokens for older consumers. New controls use `contrast`.
export const PLUS_COLORS = {
  // Equal stops preserve older background-image consumers as a flat fill.
  backgroundImage: 'linear-gradient(#616161, #616161)',
  hoverBackgroundImage: 'linear-gradient(#383838, #383838)',
  textColorPurple: '#616161',
  backgroundColorPurple: 'rgba(128, 128, 128, 0.08)',
  borderColorPurple: '#808080',
  textShadow: 'none',
  textColor: '#FFFFFF',
  iconColor: '#FFFFFF',
};

/**
 * Amber used by spend/credit warnings. Deliberately theme-independent: the
 * per-theme `accent_color_primary` is a *background* token that collapses into
 * the page on the dark themes (black defaults it to #17191A), so anything that
 * has to stay legible as a warning cannot be built from it.
 */
export const WARNING_COLORS = {
  base: '#F59E0B',
  hover: '#FBBF24',
  textColor: '#231400',
};

// Keep theme IDs stable for saved preferences and synchronize primary surface
// changes with the pre-hydration backgrounds in bootTheme.js.
// Reading text (including muted labels and placeholders) targets >= 4.5:1 on
// the shared page, card, navigation, and message surfaces in every palette.
export const Themes = {
  white: {
    // Default light theme
    solid_color_primary: "#fff",
    solid_color_secondary: "#e2e8ec",
    solid_color_secondary_dark: "#d9e0e5",
    solid_color_tertiary: "#f0f2f5",
    solid_color_block: "#f5f8fa",
    solid_color_toast: "#232425",
    solid_color_tooltip: "#21252B",
    solid_color_bubble: "#E9E9EB",
    solid_color_bubble_blue: "#4390f5",
    solid_color_user_bubble: "#f4f4f4",
    solid_color_highlight: "rgb(255 237 0 / 15%)",
    solid_color_noti: "#ff0000",

    text_color_primary: "#2d3436",
    text_color_secondary: "#4b4f55",
    text_color_tertiary: "#596167",
    text_color_alt: "#e2e8ec",
    text_color_link: "#3157a4",

    border_color_secondary: "#ececed",
    border_color_input: "#CED0D4",
    accent_color_primary: "#3696F5",
    color_input_placeholder: "#596167",
    navigation_background: "#fff",
    status_bar_style: "dark",
    color_destructive: "#FF3B30",
    color_success: "#22C55E",
  },
  ocean: {
    solid_color_primary: "#F0F8FF", // Alice blue background
    solid_color_secondary: "#E1F1FF", // Lighter sky blue secondary
    solid_color_secondary_dark: "#CCE4FF", // Soft sky blue
    solid_color_tertiary: "#E5F3FF", // Very light azure
    solid_color_block: "#F5FBFF", // Almost white with blue tint
    solid_color_toast: "#3e617e", // Steel blue for toasts
    solid_color_tooltip: "#1A3D5C",
    solid_color_bubble: "#D4E9FF", // Light azure bubble
    solid_color_bubble_blue: "#89CFF0", // Baby blue
    solid_color_user_bubble: "#E8F4FF", // Very subtle blue tint for user bubbles
    solid_color_highlight: "rgba(135, 206, 235, 0.15)", // Sky blue highlight
    solid_color_noti: "#87CEEB", // Sky blue notification

    text_color_primary: "#284e73",
    text_color_secondary: "#355975",
    text_color_tertiary: "#46647d",
    text_color_alt: "#E1F1FF", // Light sky blue for alternate text
    text_color_link: "#1d4ed8",

    border_color_secondary: "#CCE4FF", // Soft sky blue border
    border_color_input: "#D4E9FF", // Light azure border
    accent_color_primary: "#6CB4EE", // Cornflower blue accent
    color_input_placeholder: "#46647d",
    navigation_background: "#F0F8FF", // Alice blue background
    status_bar_style: "dark",
    color_destructive: "#FF3B30",
    color_success: "#22C55E",
  },
  pastel: {
    solid_color_primary: "#FFF5F9", // Very light pink background
    solid_color_secondary: "#F8E1EA", // Light pink secondary
    solid_color_secondary_dark: "#F0D4E3", // Slightly darker pink
    solid_color_tertiary: "#feebff",
    solid_color_block: "#FEFAFF", // Almost white with a hint of pink
    solid_color_toast: "#705776", // Muted purple for toasts
    solid_color_tooltip: "#4A2D58",
    solid_color_bubble: "#E8D3F7", // Light purple bubble
    solid_color_bubble_blue: "#B5A6E8", // Soft periwinkle
    solid_color_user_bubble: "#FDF0FB", // Very subtle pink tint for user bubbles
    solid_color_highlight: "rgba(255, 182, 193, 0.15)", // Light pink highlight
    solid_color_noti: "#FF9ECD", // Soft pink notification

    text_color_primary: "#594460",
    text_color_secondary: "#674e6e",
    text_color_tertiary: "#6f5676",
    text_color_alt: "#F8E1EA", // Light pink for alternate text
    text_color_link: "#3157a4",

    border_color_secondary: "#F0D4E3", // Light pink border
    border_color_input: "#E8D3F7", // Light purple border
    accent_color_primary: "#D4A5E8", // Soft purple accent
    color_input_placeholder: "#6f5676",
    navigation_background: "#FFF5F9", // Very light pink background
    status_bar_style: "dark",
    color_destructive: "#FF3B30",
    color_success: "#22C55E",
  },
  mint: {
    solid_color_primary: "#F2FAF6",
    solid_color_secondary: "#DCEEE4",
    solid_color_secondary_dark: "#CBE4D5",
    solid_color_tertiary: "#E6F4EC",
    solid_color_block: "#F7FCF9",
    solid_color_toast: "#40664f",
    solid_color_tooltip: "#1E4D3D",
    solid_color_bubble: "#D0E8DA",
    solid_color_bubble_blue: "#6BC09A",
    solid_color_user_bubble: "#EDF8F2",
    solid_color_highlight: "rgba(120, 200, 160, 0.15)",
    solid_color_noti: "#66BB8A",

    text_color_primary: "#2D5942",
    text_color_secondary: "#365b45",
    text_color_tertiary: "#486652",
    text_color_alt: "#DCEEE4",
    text_color_link: "#1d4ed8",

    border_color_secondary: "#CBE4D5",
    border_color_input: "#D0E8DA",
    accent_color_primary: "#6BBF8E",
    color_input_placeholder: "#486652",
    navigation_background: "#F2FAF6",
    status_bar_style: "dark",
    color_destructive: "#FF3B30",
    color_success: "#22C55E",
  },
  muted: {
    // A close neutral charcoal range separates surfaces without bright panels.
    solid_color_primary: "#1C1C1C", // Main reading surface
    solid_color_secondary: "#303030", // Selected controls and elevated surfaces
    solid_color_secondary_dark: "#2A2A2A",
    solid_color_tertiary: "#262626", // Slim rail and quiet hover surfaces
    solid_color_block: "#202020", // Sidebar and cards
    solid_color_toast: "#E4E4E4",
    solid_color_tooltip: "#333333",
    solid_color_bubble: "#303030",
    solid_color_bubble_blue: "#4390f5", // Accent blue for special bubbles
    solid_color_user_bubble: "#303030",
    solid_color_highlight: "rgba(255, 255, 255, 0.06)",
    solid_color_noti: "#4B4B4B",

    text_color_primary: "#E0E0E0",
    text_color_secondary: "#B8B8B8",
    text_color_tertiary: "#A3A3A3",
    text_color_alt: "#1C1C1C",
    text_color_link: "#70B5FF",

    border_color_secondary: "#343434",
    border_color_input: "#383838",
    accent_color_primary: "#4390f5", // Bright blue accent
    color_input_placeholder: "#A3A3A3",

    navigation_background: "#202020",
    status_bar_style: "light",
    color_destructive: "#FF3B30",
    color_success: "#22C55E",
  },
  paper: {
    solid_color_primary: "#FAF7F0", // Warm off-white, like natural paper
    solid_color_secondary: "#EBE6D9", // Slightly darker warm tone
    solid_color_secondary_dark: "#E2DBC7", // Deeper warm tone for contrast
    solid_color_tertiary: "#F4F0E5", // Light warm tone for tertiary elements
    solid_color_block: "#F8F4EA", // Very light warm background
    solid_color_toast: "#5C5648", // Warm dark gray for toasts
    solid_color_tooltip: "#3D362E",
    solid_color_bubble: "#EDE8DB", // Soft warm tone for bubbles
    solid_color_bubble_blue: "#4390f5",
    solid_color_user_bubble: "#F5F2E9", // Very subtle warm tint for user bubbles
    solid_color_noti: "#8B7355", // Warm brown for notifications

    text_color_primary: "#2A2722", // Deep warm brown for primary text
    text_color_secondary: "#504a3f",
    text_color_tertiary: "#655d50",
    text_color_alt: "#FAF7F0", // Warm off-white for alternate text
    text_color_link: "#3157a4",

    border_color_secondary: "#E5DFD1", // Soft warm border
    border_color_input: "#DED7C4", // Slightly darker warm border for inputs

    color_input_placeholder: "#655d50",

    navigation_background: "#F8F4EA", // Very light warm background
    status_bar_style: "dark",
    color_destructive: "#FF3B30",
    color_success: "#22C55E",
  },
  sepia: {
    solid_color_primary: "#2C261D", // Deep sepia background
    solid_color_secondary: "#3D3528", // Rich brown secondary
    solid_color_secondary_dark: "#4A402F", // Deeper vintage brown for contrast
    solid_color_tertiary: "#352E23", // Subtle dark sepia for tertiary elements
    solid_color_block: "#251F17", // Very dark sepia background
    solid_color_toast: "#D4C8B8", // Light sepia for toasts
    solid_color_tooltip: "#524838",
    solid_color_bubble: "#463B2D", // Warm brown for bubbles
    solid_color_bubble_blue: "#5B7A99", // Muted blue-gray for special bubbles
    solid_color_user_bubble: "#3D3528", // Elevated warm brown for user bubbles
    solid_color_highlight: "rgba(255, 249, 235, 0.1)", // Subtle warm highlight
    solid_color_noti: "#7D6E5B", // Muted sepia notification color

    text_color_primary: "#D4C8B6",
    text_color_secondary: "#C7B79F",
    text_color_tertiary: "#BEAE98",
    text_color_alt: "#2C261D", // Dark sepia for alternate text
    text_color_link: "#7CB8FA",

    border_color_secondary: "#463B2D", // Rich sepia border
    border_color_input: "#544733", // Slightly lighter border for inputs
    accent_color_primary: "#8B7355", // Warm brown accent
    color_input_placeholder: "#BEAE98",

    navigation_background: "#251F17", // Very dark sepia background
    status_bar_style: "light",
    color_destructive: "#FF3B30",
    color_success: "#22C55E",
  },
  night: {
    solid_color_primary: "#1A1D2E", // Deep night blue background
    solid_color_secondary: "#2C3347", // Rich midnight blue secondary
    solid_color_secondary_dark: "#252B3D", // Deeper blue for contrast
    solid_color_tertiary: "#212534", // Subtle dark blue for tertiary elements
    solid_color_block: "#151825", // Very dark blue background
    solid_color_toast: "#E4E6F0", // Soft blue-white for toasts
    solid_color_tooltip: "#3B4462",
    solid_color_bubble: "#2F364D", // Deep blue-gray for bubbles
    solid_color_bubble_blue: "#4A6FA5", // Muted blue for special bubbles
    solid_color_user_bubble: "#2C3347", // Elevated midnight blue for user bubbles
    solid_color_highlight: "rgba(147, 190, 255, 0.1)", // Subtle blue highlight
    solid_color_noti: "#4A6FA5", // Muted blue notification color

    text_color_primary: "#CAD1E2",
    text_color_secondary: "#B4BDD2",
    text_color_tertiary: "#A0A9C3",
    text_color_alt: "#1A1D2E", // Dark blue for alternate text
    text_color_link: "#58a6ff",

    border_color_secondary: "#2F364D", // Rich blue-gray border
    border_color_input: "#363D57", // Slightly lighter border for inputs
    accent_color_primary: "#5C82BD", // Muted blue accent
    color_input_placeholder: "#A0A9C3",

    navigation_background: "#151825", // Very dark blue background
    status_bar_style: "light",
    color_destructive: "#FF3B30",
    color_success: "#22C55E",
  },
  smoke: {
    solid_color_primary: "#13171B", // Deep charcoal background
    solid_color_secondary: "#2A2E32", // Rich smoke gray secondary
    solid_color_secondary_dark: "#232629", // Deeper smoke for contrast
    solid_color_tertiary: "#1A1E22", // Subtle dark smoke for tertiary elements
    solid_color_block: "#0F1214", // Very dark smoke background
    solid_color_toast: "#E5E5E5", // Light smoke for toasts
    solid_color_tooltip: "#414850",
    solid_color_bubble: "#363A3E", // Medium smoke for bubbles
    solid_color_bubble_blue: "#4F7693", // Muted blue-gray for special bubbles
    solid_color_user_bubble: "#2A2E32", // Elevated charcoal for user bubbles
    solid_color_highlight: "rgba(229, 229, 229, 0.1)", // Subtle smoke highlight
    solid_color_noti: "#4F5559", // Muted smoke notification color

    text_color_primary: "#CCD1D5",
    text_color_secondary: "#B5BDC3",
    text_color_tertiary: "#A4ABB1",
    text_color_alt: "#13171B", // Dark smoke for alternate text
    text_color_link: "#68adff",

    border_color_secondary: "#2A2E32", // Rich smoke border
    border_color_input: "#363A3E", // Slightly lighter border for inputs
    accent_color_primary: "#607D94", // Muted blue-gray accent
    color_input_placeholder: "#A4ABB1",

    navigation_background: "#0F1214", // Very dark smoke background
    status_bar_style: "light",
    color_destructive: "#FF3B30",
    color_success: "#22C55E",
  },
  black: {
    solid_color_primary: "#000000",
    solid_color_secondary: "#191919",
    solid_color_secondary_dark: "#0e0e0e",
    solid_color_tertiary: "#121212",
    solid_color_block: "#000000",
    solid_color_toast: "#fcfcfc",
    solid_color_tooltip: "#2B2B30",
    solid_color_bubble: "#242628", // Quiet elevation that keeps muted text readable
    solid_color_user_bubble: "#1A1A1A", // Subtle elevation for user bubbles

    // True black needs a dimmer foreground than the elevated dark palettes.
    text_color_primary: "#B0B5BA",
    text_color_secondary: "#A2ABB3",
    text_color_tertiary: "#959BA1",
    text_color_alt: "#000",
    text_color_link: "#58a6ff",
    solid_color_bubble_blue: "#4390f5",
    
    border_color_secondary: "#17191A",
    border_color_input: "#17191A",

    color_input_placeholder: "#959BA1",

    navigation_background: "#000000",
    status_bar_style: "light",
    color_destructive: "#FF3B30",
    color_success: "#22C55E",
  },
};

export const THEME_DEFAULT_ACCENT = {
  white:  Themes.white.solid_color_secondary,
  ocean:  Themes.ocean.solid_color_secondary_dark,
  pastel: Themes.pastel.solid_color_secondary,
  mint:   Themes.mint.solid_color_secondary,
  paper:  Themes.paper.solid_color_secondary,
  sepia:  Themes.sepia.solid_color_secondary_dark,
  muted:  Themes.muted.solid_color_secondary,
  night:  Themes.night.border_color_input,
  smoke:  Themes.smoke.border_color_input,
  black:  Themes.black.border_color_input,
};

export const getShadowThemeStyles = (theme) => {
  // Floating controls use their fill and border for separation, without a halo.
  const styles = {
    'white': {
      primaryOpaque: (opacity) => `rgba(255, 255, 255, ${opacity})`,
      borderColor: 'rgba(227, 227, 227, 0.8)',
      backgroundColor: 'rgba(250, 250, 250, 0.80)',
      boxShadow: 'none',
      placeholderColor: Themes.white.color_input_placeholder,
    },
    'light': {
      primaryOpaque: (opacity) => `rgba(255, 255, 255, ${opacity})`,
      borderColor: 'rgba(227, 227, 227, 0.8)',
      backgroundColor: 'rgba(250, 250, 250, 0.80)',
      boxShadow: 'none',
      placeholderColor: Themes.white.color_input_placeholder,
    },
    'ocean': {
      primaryOpaque: (opacity) => `rgba(240, 248, 255, ${opacity})`,
      borderColor: 'rgba(207, 232, 255, 0.85)',
      backgroundColor: 'rgba(230, 245, 255, 0.85)',
      boxShadow: 'none',
      placeholderColor: Themes.ocean.color_input_placeholder,
    },
    'pastel': {
      primaryOpaque: (opacity) => `rgba(255, 245, 249, ${opacity})`,
      borderColor: 'rgba(240, 212, 227, 0.85)',
      backgroundColor: 'rgba(255, 234, 242, 0.85)',
      boxShadow: 'none',
      placeholderColor: Themes.pastel.color_input_placeholder,
    },
    'mint': {
      primaryOpaque: (opacity) => `rgba(242, 250, 246, ${opacity})`,
      borderColor: 'rgba(203, 228, 213, 0.85)',
      backgroundColor: 'rgba(230, 244, 236, 0.85)',
      boxShadow: 'none',
      placeholderColor: Themes.mint.color_input_placeholder,
    },
    'muted': {
      primaryOpaque: (opacity) => `rgba(28, 28, 28, ${opacity})`,
      borderColor: 'rgba(64, 64, 64, 0.75)',
      backgroundColor: 'rgba(40, 40, 40, 0.94)',
      boxShadow: 'none',
      placeholderColor: Themes.muted.color_input_placeholder,
    },
    'black': {
      primaryOpaque: (opacity) => `rgba(0, 0, 0, ${opacity})`,
      borderColor: 'rgba(50, 50, 50, 0.6)',
      backgroundColor: 'rgba(37, 37, 37, 0.8)',
      boxShadow: 'none',
      placeholderColor: Themes.black.color_input_placeholder,
    },
    'night': {
      primaryOpaque: (opacity) => `rgba(26, 29, 46, ${opacity})`,
      borderColor: 'rgba(47, 54, 77, 0.8)',
      backgroundColor: 'rgba(43, 49, 76, 0.8)',
      boxShadow: 'none',
      placeholderColor: Themes.night.color_input_placeholder,
    },
    'paper': {
      primaryOpaque: (opacity) => `rgba(250, 247, 240, ${opacity})`,
      borderColor: 'rgba(213, 202, 186, 0.8)',
      backgroundColor: 'rgba(248, 244, 234, 0.8)',
      boxShadow: 'none',
      placeholderColor: Themes.paper.color_input_placeholder,
    },
    'sepia': {
      primaryOpaque: (opacity) => `rgba(44, 38, 29, ${opacity})`,
      borderColor: 'rgba(70, 59, 45, 0.8)',
      backgroundColor: 'rgba(60, 52, 38, 0.8)',
      boxShadow: 'none',
      placeholderColor: Themes.sepia.color_input_placeholder,
    },
    'smoke': {
      primaryOpaque: (opacity) => `rgba(19, 23, 27, ${opacity})`,
      borderColor: 'rgba(67, 67, 67, 0.8)',
      backgroundColor: 'rgba(46, 46, 46, 0.8)',
      boxShadow: 'none',
      placeholderColor: Themes.smoke.color_input_placeholder,
    },
  };

  return styles[theme] || styles['white'];
};

export const getMentionColor = (theme, colors) => {
  return isDarkTheme(theme) ? 'rgba(0, 128, 255, 0.5)' : colors.solid_color_secondary_dark;
};

// Add shared helper for agent image filter base
export const getAgentImageBaseFilter = (theme, isImageInvertable) => {
  if (!isImageInvertable) return '';
  if (theme === 'white' || theme === 'ocean' || theme === 'pastel' || theme === 'mint') return 'invert(1)';
  if (theme === 'paper') return 'sepia(1) invert(1)';
  if (theme === 'sepia') return 'sepia(1)';
  return '';
};

// Deterministic gradient picker used for music explore items (and any other
// item type that wants a colorful fallback). The gradient is keyed off the
// item's id so the same item always renders the same colors regardless of
// where in the app it's displayed (cards, lightbox, model showcase, …).
//
// Important: this is the single source of truth — explore items get a
// `gradient` field (and `gradientDark` flag) stamped onto them in
// `useExploreStore` on fetch/import, and components should prefer those
// over re-deriving here. The pickers below are exported as a fallback for
// one-off cases (e.g. an item that hasn't been hydrated through the store).
//
// Each entry is `{ colors: [...], dark: true }` where `dark: true` means
// the gradient is light enough that white text/icons disappear and dark
// foreground reads better. The flag was hand-marked once when the palette
// was authored — no per-render compute required.

function pickMusicGradientEntry(id) {
  if (!id) return MUSIC_GRADIENTS[0];
  let hash = 0;
  const s = String(id);
  for (let i = 0; i < s.length; i++) {
    hash = ((hash << 5) - hash) + s.charCodeAt(i);
    hash |= 0;
  }
  return MUSIC_GRADIENTS[Math.abs(hash) % MUSIC_GRADIENTS.length];
}

// Returns the colors array for the gradient (backward-compatible API).
export function pickMusicGradient(id) {
  return pickMusicGradientEntry(id).colors;
}

// Returns `{ colors, dark }` so callers that need to pick a foreground text
// color can read `.dark` directly without recomputing luminance.
export function pickMusicGradientMeta(id) {
  return pickMusicGradientEntry(id);
}

export const MUSIC_GRADIENTS = [
  // ── 2-stop originals ──────────────────────────────────────────────────────
  { colors: ['#667eea', '#764ba2'] },
  { colors: ['#f093fb', '#f5576c'] },
  { colors: ['#4facfe', '#00f2fe'], dark: true },
  { colors: ['#43e97b', '#38f9d7'], dark: true },
  { colors: ['#fa709a', '#fee140'], dark: true },
  { colors: ['#a18cd1', '#fbc2eb'], dark: true },
  { colors: ['#fccb90', '#d57eeb'], dark: true },
  { colors: ['#e0c3fc', '#8ec5fc'], dark: true },
  { colors: ['#ff9a9e', '#fecfef'], dark: true },
  { colors: ['#a1c4fd', '#c2e9fb'], dark: true },
  { colors: ['#d4fc79', '#96e6a1'], dark: true },
  { colors: ['#84fab0', '#8fd3f4'], dark: true },
  { colors: ['#cfd9df', '#e2ebf0'], dark: true },
  { colors: ['#fbc2eb', '#a6c1ee'], dark: true },
  { colors: ['#fdcbf1', '#e6dee9'], dark: true },
  { colors: ['#a6c0fe', '#f68084'], dark: true },
  { colors: ['#e6b980', '#eacda3'], dark: true },
  { colors: ['#d299c2', '#fef9d7'], dark: true },
  { colors: ['#89f7fe', '#66a6ff'], dark: true },
  { colors: ['#fddb92', '#d1fdff'], dark: true },
  { colors: ['#9890e3', '#b1f4cf'], dark: true },
  { colors: ['#ebc0fd', '#d9ded8'], dark: true },
  { colors: ['#f6d365', '#fda085'], dark: true },
  { colors: ['#96fbc4', '#f9f586'], dark: true },
  { colors: ['#cd9cf2', '#f6f3ff'], dark: true },
  { colors: ['#e8198b', '#c7eafd'] },
  { colors: ['#7028e4', '#e5b2ca'] },
  { colors: ['#09203f', '#537895'] },
  { colors: ['#ddd6f3', '#faaca8'], dark: true },
  { colors: ['#dcb0ed', '#99c99c'], dark: true },
  { colors: ['#f3e7e9', '#e3eeff'], dark: true },
  { colors: ['#c71d6f', '#d09693'] },
  { colors: ['#00c6ff', '#0072ff'] },
  { colors: ['#f857a6', '#ff5858'] },
  { colors: ['#a8ff78', '#78ffd6'], dark: true },
  { colors: ['#fc5c7d', '#6a82fb'] },
  { colors: ['#ee9ca7', '#ffdde1'], dark: true },
  { colors: ['#bdc3c7', '#2c3e50'] },
  { colors: ['#de6262', '#ffb88c'], dark: true },

  // ── 2-stop new ────────────────────────────────────────────────────────────
  { colors: ['#0f2027', '#203a43'] },
  { colors: ['#1a1a2e', '#e94560'] },
  { colors: ['#11998e', '#38ef7d'] },
  { colors: ['#f7971e', '#ffd200'], dark: true },
  { colors: ['#c94b4b', '#4b134f'] },
  { colors: ['#360033', '#0b8793'] },
  { colors: ['#3a1c71', '#ffaf7b'] },
  { colors: ['#004ff9', '#fff94c'] },
  { colors: ['#f8b500', '#ff006e'], dark: true },
  { colors: ['#12c2e9', '#c471ed'], dark: true },
  { colors: ['#40e0d0', '#ff8c00'], dark: true },
  { colors: ['#a80077', '#66ff00'] },
  { colors: ['#fc466b', '#3f5efb'] },
  { colors: ['#b24592', '#f15f79'] },
  { colors: ['#00b4db', '#0083b0'] },
  { colors: ['#f79d00', '#64f38c'], dark: true },
  { colors: ['#cc2b5e', '#753a88'] },
  { colors: ['#5c258d', '#4389a2'] },
  { colors: ['#005c97', '#363795'] },

  // ── 3-stop ────────────────────────────────────────────────────────────────
  { colors: ['#f953c6', '#b91d73', '#5c1a8c'] },
  { colors: ['#00f5a0', '#00d9f5', '#0051d5'], dark: true },
  { colors: ['#f7971e', '#ffd200', '#ff6b6b'], dark: true },
  { colors: ['#c471f5', '#fa71cd', '#f7971e'] },
  { colors: ['#12c2e9', '#c471ed', '#f64f59'], dark: true },
  { colors: ['#43cea2', '#185a9d', '#0f2027'] },
  { colors: ['#ee0979', '#ff6a00', '#ffcc00'], dark: true },
  { colors: ['#6a3093', '#a044ff', '#39d5ff'] },
  { colors: ['#00c9ff', '#92fe9d', '#fddb92'], dark: true },
  { colors: ['#f7971e', '#ffd200', '#a8ff78'], dark: true },
  { colors: ['#a18cd1', '#fbc2eb', '#ffecd2'], dark: true },
  { colors: ['#ffecd2', '#fcb69f', '#ff9a9e'], dark: true },
  { colors: ['#667eea', '#764ba2', '#f093fb'] },
  { colors: ['#4facfe', '#00f2fe', '#43e97b'], dark: true },
  { colors: ['#0cebeb', '#20e3b2', '#29ffc6'], dark: true },
  { colors: ['#f093fb', '#f5576c', '#4facfe'] },
  { colors: ['#30cfd0', '#330867', '#a8edea'] },
  { colors: ['#d9afd9', '#97d9e1', '#ffdde1'], dark: true },
  { colors: ['#f6d365', '#fda085', '#f093fb'], dark: true },
  { colors: ['#a1ffce', '#faffd1', '#a1ffce'], dark: true },

  // ── 4-stop ────────────────────────────────────────────────────────────────
  { colors: ['#f953c6', '#b91d73', '#7028e4', '#e5b2ca'] },
  { colors: ['#00f5a0', '#00d9f5', '#6a3093', '#a044ff'] },
  { colors: ['#f7971e', '#ffd200', '#f093fb', '#f5576c'], dark: true },
  { colors: ['#12c2e9', '#c471ed', '#f64f59', '#ff6b6b'], dark: true },
  { colors: ['#43e97b', '#38f9d7', '#4facfe', '#00f2fe'], dark: true },
  { colors: ['#ee0979', '#ff6a00', '#f7971e', '#ffd200'], dark: true },
  { colors: ['#a18cd1', '#fbc2eb', '#f6d365', '#fda085'], dark: true },
  { colors: ['#667eea', '#764ba2', '#f093fb', '#f5576c'] },
  { colors: ['#0cebeb', '#20e3b2', '#29ffc6', '#a8ff78'], dark: true },
  { colors: ['#fc5c7d', '#6a82fb', '#c471ed', '#12c2e9'] },
  { colors: ['#ffecd2', '#fcb69f', '#ff9a9e', '#f093fb'], dark: true },
  { colors: ['#30cfd0', '#330867', '#a18cd1', '#fbc2eb'] },
  { colors: ['#4facfe', '#00f2fe', '#43e97b', '#f7971e'], dark: true },
  { colors: ['#f6d365', '#fda085', '#fc5c7d', '#6a82fb'] },
  { colors: ['#d9afd9', '#97d9e1', '#a8edea', '#ffecd2'], dark: true },
];
